import React, { useState, useCallback, useEffect, useMemo } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { showLoader, hideLoader } from "../../redux/loader/actions";
import menuApi from "../../services/menu";
import { setMenuItems } from "../../redux/menu/actions";
import {
  setEncryptedCookie,
  getDecryptedCookie,
  removeCookie,
} from "../../utils";

const Navbar = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentMenu, setCurrentMenu] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  const menuItems = useSelector((state) =>
    state.menuReducer.items
      .filter((item) => item.access_type !== "none")
      .sort((a, b) => a.menu_order - b.menu_order)
  );

  const id = getDecryptedCookie("id");

  const location = useLocation();

  const getAllMenu = useCallback(
    (id) => {
      if (!id) return;
      dispatch(showLoader());
      const onSuccess = (res) => {
        dispatch(hideLoader());
        if (res?.data?.status) {
          const items = res.data.data || [];
          dispatch(setMenuItems(items));
        }
      };
      const onFailure = (err) => {
        dispatch(hideLoader());
        console.error("Error fetching menu:", err);
      };

      menuApi.userBasedMenu(id).then(onSuccess, onFailure);
    },
    [dispatch]
  );

  useEffect(() => {
    if (id) {
      getAllMenu(id);
    }
  }, [id, getAllMenu]);

  const handleClick = (event, menuName) => {
    setAnchorEl(event.currentTarget);
    setCurrentMenu(menuName);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setCurrentMenu(null);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDropdownToggle = (menuName) => {
    setOpenDropdown((prev) => (prev === menuName ? null : menuName));
  };

  const memoizedMenuItems = useMemo(() => menuItems, [menuItems]);
  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const isActive = (link, menuName) => {
    if (location.pathname === link) {
      return { fontWeight: "bold", color: "#a00" };
    }

    const menu = memoizedMenuItems.find((menu) => menu.name === menuName);
    if (menu && Array.isArray(menu.submenus) && menu.submenus.length > 0) {
      return menu.submenus.some((submenu) => location.pathname === submenu.link)
        ? { fontWeight: "bold", color: "#a00" }
        : {};
    }
    if (location.pathname.startsWith(link)) {
      return { fontWeight: "bold", color: "#a00" };
    }

    return {};
  };

  const sortedSubmenus = (submenus) =>
    submenus ? submenus.sort((a, b) => a.menu_order - b.menu_order) : [];

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (memoizedMenuItems.length <= 0) {
        window.location.reload();
      }
    }, 3000);

    return () => clearInterval(intervalId);
  }, [memoizedMenuItems]);

  return (
    <>
      <AppBar
        position="static"
        sx={{ backgroundColor: "white", color: "black" }}
      >
        <Toolbar sx={{ justifyContent: { xs: "space-between", md: "center" } }}>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={handleDrawerToggle}
            sx={{ display: { xs: "block", md: "none" } }}
          >
            <MenuIcon sx={{ color: "black" }} />
          </IconButton>


          {Array.isArray(memoizedMenuItems) && memoizedMenuItems.length > 0
            ? memoizedMenuItems.map((menu) =>
              menu.dropdown ? (
                <div key={menu.id} style={{ position: "relative" }}>
                  <Button
                    color="inherit"
                    aria-controls={
                      currentMenu === menu.name ? "menu-list" : undefined
                    }
                    aria-haspopup="true"
                    aria-expanded={
                      currentMenu === menu.name ? "true" : undefined
                    }
                    onClick={(event) => handleClick(event, menu.name)}
                    sx={{
                      display: { xs: "none", sm: "none", md: "flex" },
                      mx: 2,
                      color: "black",
                      whiteSpace: "nowrap",
                      alignItems: "center",
                      ...isActive(menu.link, menu.name),
                    }}
                  >
                    {menu.name}
                    <ArrowDropDownIcon sx={{ ml: 1, color: "black" }} />
                  </Button>
                  <Menu
                    id="menu-list"
                    anchorEl={anchorEl}
                    open={currentMenu === menu.name}
                    onClose={handleClose}
                    sx={{
                      "& .MuiMenuItem-root": {
                        color: "black",
                      },
                    }}
                  >
                    {sortedSubmenus(menu.submenus).map((item) => (
                      <MenuItem
                        key={item.id}
                        onClick={handleClose}
                        component={Link}
                        to={item.link}
                        sx={isActive(item.link)}
                      >
                        {item.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
              ) : (
                <Button
                  key={menu.id}
                  color="inherit"
                  component={Link}
                  to={menu.link}
                  sx={{
                    display: { xs: "none", sm: "none", md: "block" },
                    mx: 2,
                    color: "black",
                    ...isActive(menu.link),
                  }}
                >
                  {menu.name}
                </Button>
              )
            )
            : null}
          {/* <Button
            color="inherit"
            component={Link}
            to="/enquiry"
            sx={{
              display: { xs: "none", sm: "none", md: "block" },
              mx: 2,
              color: "black",
              whiteSpace: "nowrap",
            }}
          >
            Enquiry
          </Button> */}

        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          width: 250,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 250,
            backgroundColor: "white",
            color: "black",
          },
        }}
      >
        <List>
          {memoizedMenuItems.map((menu) => (
            <div key={menu.id}>
              <ListItem
                button
                onClick={() => {
                  if (
                    Array.isArray(menu.submenus) &&
                    menu.submenus.length > 0
                  ) {
                    handleDropdownToggle(menu.name);
                  } else {
                    closeDrawer();
                  }
                }}
              >
                {Array.isArray(menu.submenus) && menu.submenus.length > 0 ? (
                  <Button
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    {menu.name}
                    <ArrowDropDownIcon sx={{ ml: 1, color: "black" }} />
                  </Button>
                ) : (
                  <Button
                    component={Link}
                    to={menu.link}
                    sx={{ width: "100%" }}
                  >
                    {menu.name}
                  </Button>
                )}
              </ListItem>

              {Array.isArray(menu.submenus) && menu.submenus.length > 0 && (
                <List
                  sx={{
                    display: openDropdown === menu.name ? "block" : "none",
                  }}
                >
                  {sortedSubmenus(menu.submenus).map((item) => (
                    <ListItem button key={item.id} onClick={closeDrawer}>
                      <Button
                        component={Link}
                        to={item.link}
                        sx={{
                          color: "black",
                          width: "100%",
                          ...isActive(item.link),
                        }}
                      >
                        {item.name}
                      </Button>
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          ))}
        </List>
      </Drawer>
    </>
  );
};

export default Navbar;
